import { Flex, Button } from '@workwhile/ui'
import React from 'react'
import { DateRangePickerFilter } from 'components/Filter/DateRangePickerFilter'
import { IntFilter } from 'components/Filter/IntFilter'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filtersSchema, FiltersSchema } from './filtersSchema'
import { useReviewShiftsContextValue } from '../../context/ReviewShiftsProvider'

// WorkIdFilters component
const WorkIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="workId.id"
      label="Work ID"
      filterKey="workId"
      inputLabel="Work ID"
    />
  )
}

// ShiftIdFilters component
const ShiftIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="shiftId.id"
      label="Shift"
      filterKey="shiftId"
      inputLabel="Shift ID"
    />
  )
}

// CompanyIdFilters component
const CompanyIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="companyId.id"
      label="Company ID"
      filterKey="companyId"
      inputLabel="Company ID"
    />
  )
}

// CompanyGroupIdFilters component
const CompanyGroupIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="companyGroupId.id"
      label="Company Group ID"
      filterKey="companyGroupId"
      inputLabel="Company Group ID"
    />
  )
}

// WorkerIdFilters component
const WorkerIdFilters = () => {
  return (
    <IntFilter<FiltersSchema>
      fieldPath="workerId.id"
      label="Worker ID"
      filterKey="workerId"
      inputLabel="Worker ID"
    />
  )
}

interface ReviewFiltersProps {
  hideWorkerFilter?: boolean
}

function buildFormValues(
  filters: {
    workId: number | null
    shiftId: number | null
    companyId: number | null
    companyGroupId: number | null
    workerId: number | null
  },
  timeRangeFilters: {
    startDate?: Date
    endDate?: Date
  },
  hideWorkerFilter: boolean
): FiltersSchema {
  return {
    timeRange: {
      dateRange: {
        from: timeRangeFilters.startDate || null,
        to: timeRangeFilters.endDate || null,
      },
    },
    workId: {
      id: filters.workId || null,
    },
    shiftId: {
      id: filters.shiftId || null,
    },
    companyId: {
      id: filters.companyId || null,
    },
    companyGroupId: {
      id: filters.companyGroupId || null,
    },
    workerId: {
      id: hideWorkerFilter ? null : filters.workerId || null,
    },
  }
}

export const ReviewFilters = ({
  hideWorkerFilter = false,
}: ReviewFiltersProps) => {
  const {
    state: { filters, timeRangeFilters },
    actions: { setFilters, setTimeRangeFilters },
  } = useReviewShiftsContextValue()

  const formValues = buildFormValues(
    filters,
    timeRangeFilters,
    hideWorkerFilter
  )

  const methods = useForm<FiltersSchema>({
    resolver: zodResolver(filtersSchema),
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: formValues,
    values: formValues,
  })

  const onSubmit = (data: FiltersSchema) => {
    // Update filters
    setFilters({
      workId: data.workId.id,
      shiftId: data.shiftId.id,
      companyId: data.companyId.id,
      companyGroupId: data.companyGroupId.id,
      workerId: hideWorkerFilter ? null : data.workerId.id,
    })

    // Update time range filters
    setTimeRangeFilters({
      startDate: data.timeRange.dateRange.from || undefined,
      endDate: data.timeRange.dateRange.to || undefined,
    })
  }

  return (
    <FormProvider {...methods}>
      <Flex
        as="form"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <WorkIdFilters />
        <DateRangePickerFilter<FiltersSchema>
          dateRangeFieldPath="timeRange.dateRange"
          label="Time Range"
          filterKey="time-range"
          placeholder="Select Date range"
        />
        {!hideWorkerFilter && <WorkerIdFilters />}
        <ShiftIdFilters />
        <CompanyIdFilters />
        <CompanyGroupIdFilters />
        <Button type="submit" variant="primary">
          Search
        </Button>
      </Flex>
    </FormProvider>
  )
}
